import { createAsyncThunk } from '@reduxjs/toolkit';
import api, { handleApiError } from 'state/api';

const unwrapApiData = (response, rejectWithValue) => {
  const body = response?.data;
  if (!body?.success) {
    return rejectWithValue({
      message: body?.message || body?.error || 'Request failed',
    });
  }
  return body.data ?? body;
};

export const getCreationSettingsApi = createAsyncThunk(
  'listingCreation/getSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('listing-creation/settings');
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('getCreationSettingsApi', error, rejectWithValue);
    }
  },
);

export const updateCreationSettingsApi = createAsyncThunk(
  'listingCreation/updateSettings',
  async (settings, { rejectWithValue }) => {
    try {
      const response = await api.put('listing-creation/settings', settings);
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('updateCreationSettingsApi', error, rejectWithValue);
    }
  },
);

export const getCreationProcessStatusApi = createAsyncThunk(
  'listingCreation/getProcessStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('listing-creation/process-status');
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('getCreationProcessStatusApi', error, rejectWithValue);
    }
  },
);

export const getCreationMappingInfoApi = createAsyncThunk(
  'listingCreation/getMappingInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('listing-creation/mapping');
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('getCreationMappingInfoApi', error, rejectWithValue);
    }
  },
);

export const uploadCreationMappingApi = createAsyncThunk(
  'listingCreation/uploadMapping',
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('listing-creation/mapping/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('uploadCreationMappingApi', error, rejectWithValue);
    }
  },
);

export const startCreationRunApi = createAsyncThunk(
  'listingCreation/startRun',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post('listing-creation/run', payload);
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('startCreationRunApi', error, rejectWithValue);
    }
  },
);

export const stopCreationRunApi = createAsyncThunk(
  'listingCreation/stopRun',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('listing-creation/run/stop');
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('stopCreationRunApi', error, rejectWithValue);
    }
  },
);

export const getCreationStorePolicyReadinessApi = createAsyncThunk(
  'listingCreation/getStorePolicyReadiness',
  async (storeId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `listing-creation/store-policy-readiness?storeId=${encodeURIComponent(storeId)}`,
      );
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('getCreationStorePolicyReadinessApi', error, rejectWithValue);
    }
  },
);

export const getCreationScopeCountApi = createAsyncThunk(
  'listingCreation/getScopeCount',
  async ({ scopeMode, partslinks, limit }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (scopeMode) params.append('scopeMode', scopeMode);
      if (partslinks?.length) params.append('partslinks', partslinks.join(','));
      if (limit != null) params.append('limit', String(limit));
      const response = await api.get(`listing-creation/scope-count?${params.toString()}`);
      return unwrapApiData(response, rejectWithValue);
    } catch (error) {
      return handleApiError('getCreationScopeCountApi', error, rejectWithValue);
    }
  },
);

export const downloadCreationRunReportCsvApi = createAsyncThunk(
  'listingCreation/downloadRunReportCsv',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('listing-creation/run-report/download', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `listing-creation-run-${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return { downloaded: true };
    } catch (error) {
      return handleApiError('downloadCreationRunReportCsvApi', error, rejectWithValue);
    }
  },
);
